import { Injectable, OnDestroy, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { HttpService } from '@tc/http';
import { EnvironmentService } from '@tc/foundation';
import { ITCResultResponse, TCErrorResponse, ITcResponse } from '../../../common/models/ITcResponse';
import { ApiSubmitPhotoVerification } from '../../../common/contracts/account/ApiSubmitPhotoVerification';
import { LoggerService, Severity } from '@tc/logger';
import { AuthenticationService } from '@tc/security-oauth-legacy';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { DropCatchWebApi } from '../../../../environments/endpoints';
import { ICreditCardData } from '../../credit-card/models/ICreditCardData';
import { AccountVerificationType } from '../models/AccountVerificationType';
import { removeSubscriptions } from '../../../common/utilities/remove-subscriptions';
import { UserAccount } from '../models/UserAccount';
import { IValidationResult } from '../../../common/validators/models/IValidationResult';
import { AccountService } from '../account.service';
import { WalletService } from '../../../common/services/wallet/wallet.service';
import { UserAccountAddress } from '../models/UserAccountAddress';
import { IDropCatchConfiguration } from '../../../site/configuration/IDropCatchConfiguration';

@Injectable()
export class CreateAccountService implements OnDestroy {
  private http = inject(HttpService);
  private httpClient = inject(HttpClient);
  private envService = inject<EnvironmentService<IDropCatchConfiguration>>(EnvironmentService);
  private authService = inject(AuthenticationService);
  private accountService = inject(AccountService);
  private walletService = inject(WalletService);
  private loggingService = inject(LoggerService);

  private accountSubscription: Subscription;
  private readonly name = 'CreateAccountService';

  isLoadingAccount$: Observable<boolean>;
  isCreatingAccount$: Observable<boolean>;
  hasAccountCreationError$: Observable<boolean>;
  hasCreditCardVaultingError$: Observable<boolean>;
  isAccountCreated$ = new BehaviorSubject<boolean>(false);
  isUploadingImages$ = new BehaviorSubject<boolean>(false);
  isVaultingCreditCard$ = new BehaviorSubject<boolean>(false);
  isCurrentStepInAccount$ = new BehaviorSubject<boolean>(false);
  isCurrentStepLogIn$ = new BehaviorSubject<boolean>(false);
  isCurrentStepInCreditCard$ = new BehaviorSubject<boolean>(false);
  isCurrentStepInImages$ = new BehaviorSubject<boolean>(false);
  isCurrentStepComplete$ = new BehaviorSubject<boolean>(false);
  isCompleteSubscription: Subscription;
  hasImageVerificationError$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.configureSubscriptions();
  }

  ngOnDestroy(): void {
    const subscriptions = [this.accountSubscription];
    removeSubscriptions(subscriptions);
  }

  private configureSubscriptions(): void {
    this.isLoadingAccount$ = this.accountService.isLoading$;
    this.isCreatingAccount$ = this.accountService.isCreating$;
    this.hasAccountCreationError$ = this.accountService.hasCreateError$;
    this.hasCreditCardVaultingError$ = this.walletService.hasCreditCardVaultingError$;
    this.accountSubscription = this.accountService.account$.subscribe((account) => {
      this.setIsCurrentStepInAccount(account);
      this.setIsCurrentStepInCreditCard(account);
      this.setIsCurrentStepInComplete(account);
    });
  }

  logIn(): void {
    this.authService.login();
  }

  updateCurrentStep(verification: AccountVerificationType): void {
    this.accountService.updateCompletedVerifications(verification);
  }

  private setIsCurrentStepInAccount(account: UserAccount): void {
    const isInAccount = account == null;
    this.isCurrentStepInAccount$.next(isInAccount);
  }

  private setIsCurrentStepInCreditCard(account: UserAccount): void {
    this.isCurrentStepLogIn$.next(false);
    const isInCreditCard =
      account != null && (account.completedVerifications == null || !account.completedVerifications.includes(AccountVerificationType.CreditCard));
    this.isCurrentStepInCreditCard$.next(isInCreditCard);
  }

  private setIsCurrentStepInComplete(account: UserAccount): void {
    this.isCurrentStepLogIn$.next(false);
    const isComplete =
      account != null && account.completedVerifications != null && account.completedVerifications.includes(AccountVerificationType.CreditCard);
    this.isCurrentStepComplete$.next(isComplete);
  }

  createAccount(form: UntypedFormGroup): Observable<boolean> {
    const account = new UserAccount();
    account.id = null;
    account.userId = null;
    account.username = form.get('userName').value;
    account.password = form.get('password').value;
    account.displayName = form.get('displayName').value;
    account.firstName = form.get('firstName').value;
    account.lastName = form.get('lastName').value;
    account.organization = form.get('organization').value;
    account.email = form.get('email').value;
    account.address = new UserAccountAddress();
    account.address.addressLine1 = form.get('addressLine1').value;
    account.address.addressLine2 = form.get('addressLine2').value;
    account.address.city = form.get('city').value;
    account.address.region = form.get('region').value;
    account.address.country = form.get('country').value;
    account.address.postalCode = form.get('postalCode').value;
    account.phoneCountryCode = form.get('phoneCountryCode').value;
    account.phoneNumber = form.get('phoneNumber').value;
    account.faxCountryCode = form.get('faxCountryCode').value;
    account.fax = form.get('fax').value;

    return this.accountService.createAccount(account).pipe(
      tap((isSuccessful) => {
        if (isSuccessful) {
          this.isCurrentStepInAccount$.next(false);
          this.isCurrentStepLogIn$.next(true);
        }
      })
    );
  }

  saveCreditCard(data: ICreditCardData): Observable<boolean> {
    this.isVaultingCreditCard$.next(true);

    this.loggingService.log(`${this.name}.saveCreditCard`, Severity.Information, { message: `Preparing to vault credit card data.`}, ['CreditCard']);

    return this.walletService.addPaymentMethodToWallet(data.payload.nonce, data.deviceData).pipe(
      tap(() => this.isVaultingCreditCard$.next(false)),
      map((response) => {
        if (response && response.success) {
          this.updateCurrentStep(AccountVerificationType.CreditCard);
          return true;
        } else {
          this.loggingService.log(`${this.name}.saveCreditCard`, Severity.Error, { message: response.errorMessage });
          return false;
        }
      }),
      catchError(() => of(false))
    );
  }

  uploadImageFormData(files: FormData): Observable<ITCResultResponse<IValidationResult> | TCErrorResponse> {
    this.isUploadingImages$.next(true);
    return this.envService.configuration$.pipe(
      switchMap((config) => {
        const url = `${config.apiUrl}${DropCatchWebApi.submitPhotoVerification.endpoint}`;
        return this.httpClient.post<ITcResponse<IValidationResult>>(url, files);
      }),
      map((response) => {
        this.isUploadingImages$.next(false);
        this.updateCurrentStep(AccountVerificationType.PhotoId);
        return response;
      }),
      catchError((error: TCErrorResponse) => {
        this.isUploadingImages$.next(false);
        return of(error);
      })
    );
  }

  clearImageVerificationError(): void {
    this.hasImageVerificationError$.next(false);
  }
}
